import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SessionUser, AuthVerifyResponse } from '@shared/types';
import { api } from '@/api/client';

interface AuthState {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  lastSessionCheck: number | null;

  // Actions
  setUser: (user: SessionUser | null) => void;
  setLoading: (loading: boolean) => void;
  login: (email: string) => Promise<void>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
  verifyLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: (force?: boolean) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      lastSessionCheck: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),

      login: async (email: string) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/request', { email });
        } finally {
          set({ isLoading: false });
        }
      },

      verifyOTP: async (email: string, otp: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<AuthVerifyResponse>('/auth/verify-otp', { email, otp });
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      verifyLink: async (token: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<AuthVerifyResponse>('/auth/verify-link', { token });
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          // Log but don't throw - we still want to clear local state
          console.error('Logout API call failed:', error);
        } finally {
          set({ user: null, isAuthenticated: false });
        }
      },

      checkSession: async (force = false) => {
        const state = get();
        const now = Date.now();
        const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

        // Skip if we checked recently and have a valid session (unless forced)
        if (
          !force &&
          state.lastSessionCheck &&
          state.isAuthenticated &&
          now - state.lastSessionCheck < SESSION_CHECK_INTERVAL
        ) {
          set({ isLoading: false });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await api.get<{ user: SessionUser }>('/auth/me');
          set({
            user: response.user,
            isAuthenticated: true,
            lastSessionCheck: now,
          });
        } catch (error) {
          // Session invalid or expired - this is expected, not an error
          // Only log in development for debugging purposes
          if (import.meta.env.DEV) {
            console.debug('Session check failed:', error);
          }
          set({
            user: null,
            isAuthenticated: false,
            lastSessionCheck: now,
          });
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'pagecraft-auth',
      partialize: (state) => ({ user: state.user }),
    }
  )
);
